import { createFileRoute, Link } from "@tanstack/react-router";
import { Star } from "lucide-react";
import { ORG_REF, PHYSICIAN_ID } from "@/lib/medical-schema";
import drFitzer from "@/assets/dr-fitzer-800.webp.asset.json";
import drFitzer480 from "@/assets/dr-fitzer-480.webp.asset.json";
import { Flower } from "@/components/site/Flower";
import { EditableText } from "@/components/site/EditableText";
import { PageSection } from "@/components/site/PageSection";
import { WeightLossOptionsButtons } from "@/components/site/WeightLossOptionsButtons";
import { canonicalAndAlternates } from "@/lib/hreflang";
import { schemaDateFields, withProfileDateTime } from "@/lib/schema-dates";

export const Route = createFileRoute("/about")({
  head: () => ({
    meta: [
      { title: "Matthew Fitzer, M.D., Bariatric Surgeon | Virginia Bariatric" },
      {
        name: "description",
        content:
          "Meet Matthew Fitzer, M.D., board-certified bariatric surgery and weight loss doctor in Northern Virginia — 6,000+ laparoscopic weight-loss operations since 2005.",
      },
      ...(import.meta.env.SSR ? [
        { property: "og:title", content: "Matthew Fitzer, M.D., Bariatric Surgeon | Virginia Bariatric" },
        { property: "og:description", content: "Meet Matthew Fitzer, M.D., board-certified bariatric surgery and weight loss doctor in Northern Virginia — 6,000+ laparoscopic weight-loss operations since 2005." },
        { property: "og:url", content: "https://virginiabariatric.com/about" },
        { property: "og:type", content: "profile" },
        { property: "profile:first_name", content: "Matthew" },
        { property: "profile:last_name", content: "Fitzer" },
        { property: "og:image", content: "https://virginiabariatric.com/__l5e/assets-v1/3d136713-9dce-42a2-87c9-b7016519b648/og-dr-fitzer.jpg" },
        { property: "og:image:secure_url", content: "https://virginiabariatric.com/__l5e/assets-v1/3d136713-9dce-42a2-87c9-b7016519b648/og-dr-fitzer.jpg" },
        { property: "og:image:type", content: "image/jpeg" },
        { property: "og:image:width", content: "1200" },
        { property: "og:image:height", content: "630" },
        { property: "og:image:alt", content: "Dr. Matthew Fitzer, board-certified bariatric surgery and weight loss doctor at Virginia Bariatric Surgery" },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:image", content: "https://virginiabariatric.com/__l5e/assets-v1/3d136713-9dce-42a2-87c9-b7016519b648/og-dr-fitzer.jpg" },
        { name: "twitter:image:alt", content: "Dr. Matthew Fitzer, board-certified bariatric surgery and weight loss doctor at Virginia Bariatric Surgery" },
        { name: "twitter:title", content: "Matthew Fitzer, M.D., Bariatric Surgeon | Virginia Bariatric" },
        { name: "twitter:description", content: "Meet Matthew Fitzer, M.D., board-certified bariatric surgery and weight loss doctor in Northern Virginia — 6,000+ laparoscopic weight-loss operations since 2005." },
      ] : []),
    ],
    links: [
      ...(import.meta.env.SSR ? canonicalAndAlternates("/about") : []),
      {
        rel: "preload",
        as: "image",
        href: drFitzer480.url,
        imageSrcSet: `${drFitzer480.url} 480w, ${drFitzer.url} 800w`,
        imageSizes: "(min-width: 768px) 30vw, 60vw",
        type: "image/webp",
        fetchPriority: "high",
      },
    ],
    scripts: import.meta.env.SSR ? [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@graph": [
            // The MedicalClinic/Organization and physician Person nodes are
            // emitted once sitewide in src/routes/__root.tsx; repeating them
            // here created two nodes per @id, which reads as a conflict.
            {
              "@type": ["AboutPage", "ProfilePage"],
              "@id": "https://virginiabariatric.com/about#webpage",
              url: "https://virginiabariatric.com/about",
              name: "Matthew Fitzer, M.D. | Bariatric Surgery & Weight Loss Doctor",
              description:
                "Meet Matthew Fitzer, M.D., the board-certified bariatric surgery and weight loss doctor at Virginia Bariatric Surgery in Herndon, Virginia.",
              inLanguage: "en-US",
              ...withProfileDateTime(schemaDateFields("/about")),
              isPartOf: ORG_REF,
              about: { "@id": PHYSICIAN_ID },
              mainEntity: { "@id": PHYSICIAN_ID },
              primaryImageOfPage: { "@id": "https://virginiabariatric.com/about#portrait" },
            },
            {
              "@type": "ImageObject",
              "@id": "https://virginiabariatric.com/about#portrait",
              contentUrl: `https://virginiabariatric.com${drFitzer.url}`,
              url: `https://virginiabariatric.com${drFitzer.url}`,
              width: 800,
              height: 1066,
              caption:
                "Portrait of Matthew Fitzer, M.D., board-certified bariatric surgeon at Virginia Bariatric Surgery",
              representativeOfPage: true,
            },
          ],
        }),

      },
    ] : [],
  }),
  component: AboutPage,
});

function AboutPage() {
  return (
    <>
      <PageSection className="relative pt-10 pb-5 grid gap-8 md:grid-cols-2 items-center">
        <Flower className="hidden md:block right-2 top-6 w-56 lg:w-64" />
        <div className="relative">
          <div className="absolute -inset-4 rounded-2xl bg-brand-soft -z-10" />
          <figure className="relative grid grid-cols-5 grid-rows-2 gap-3 aspect-[5/4]">
            <img
              src={drFitzer.url}
              srcSet={`${drFitzer480.url} 480w, ${drFitzer.url} 800w`}
              sizes="(min-width: 768px) 30vw, 60vw"
              alt="Portrait of Dr. Matthew Fitzer, board-certified bariatric surgeon at Virginia Bariatric Surgery"
              width={800}
              height={1066}
              loading="eager"
              decoding="async"
              fetchPriority="high"
              className="col-span-3 row-span-2 h-full w-full rounded-xl object-cover shadow-xl"
            />
            <img
              src="/img/dr-fitzer-surgery-400.webp"
              srcSet="/img/dr-fitzer-surgery-400.webp 400w, /img/dr-fitzer-surgery-800.webp 800w"
              sizes="(min-width: 768px) 20vw, 40vw"
              alt="Dr. Fitzer performing laparoscopic bariatric surgery in the operating room"
              width={1236}
              height={1075}
              loading="lazy"
              decoding="async"
              className="col-span-2 row-span-1 h-full w-full rounded-xl object-cover shadow-2xl ring-4 ring-background"
            />
            <img
              src="/img/dr-fitzer-patient-400.webp"
              srcSet="/img/dr-fitzer-patient-400.webp 400w, /img/dr-fitzer-patient-800.webp 800w"
              sizes="(min-width: 768px) 20vw, 40vw"
              alt="Dr. Fitzer with a grateful post-op bariatric patient"
              width={1575}
              height={1316}
              loading="lazy"
              decoding="async"
              className="col-span-2 row-span-1 h-full w-full rounded-xl object-cover shadow-2xl ring-4 ring-background"
            />

          </figure>
        </div>
        <div>
          <EditableText as="h1" contentKey="about.hero.title" defaultValue="Matthew Fitzer, M.D." className="text-4xl md:text-5xl font-serif" />
          <EditableText as="p" contentKey="about.hero.subtitle" defaultValue="Bariatric Surgeon and Physician" className="mt-1 text-lg text-muted-foreground italic" />
          {/*
            Visible-only rating badge. Google's structured-data policy forbids
            marking up ratings collected on Google itself, so this stays out of
            JSON-LD and simply links to the Google Business Profile.
          */}
          <a
            href="https://www.google.com/search?q=Dr.+Matthew+A.+Fitzer,+MD"
            target="_blank"
            rel="noopener noreferrer"
            className="mt-3 inline-flex items-center gap-2 rounded-full border border-border bg-card px-4 py-2 text-sm font-medium text-foreground/80 hover:bg-brand-soft/40"
            aria-label="Dr. Matthew Fitzer is rated 4.7 out of 5 stars from 37 Google reviews. Opens the Google Business Profile in a new tab."
          >
            <span className="flex items-center gap-0.5 text-brand-logo" aria-hidden="true">
              {[0, 1, 2, 3, 4].map((i) => (
                <Star key={i} size={15} className={i < 4 ? "fill-current" : "fill-current opacity-60"} />
              ))}
            </span>
            <span>
              <strong className="font-semibold text-foreground">4.7</strong> from 37 Google reviews
            </span>
          </a>

          <div className="mt-6 space-y-4 text-foreground/80 leading-[1.6]">
            <EditableText as="p" contentKey="about.hero.bio1-intro" multiline defaultValue="Dr. Matthew Fitzer is a fellowship-trained bariatric surgeon who has focused on providing excellent care to overweight and obese patients since 2005. Since 2012, he has been serving patients in Herndon, Reston, Fairfax, Arlington, Alexandria, Loudoun County, and the DC metro area." />
            <EditableText as="p" contentKey="about.hero.bio1-credentials" multiline defaultValue="He is certified by the American Board of Surgery and is a fellow of the American College of Surgeons and the American Society of Metabolic and Bariatric Surgeons. He has founded and served as the Medical Director of MBSAQIP-Certified bariatric programs at multiple institutions. He has authored book chapters and articles that have been published in prestigious journals including the New England Journal of Medicine.">
              {(value) => {
                const parts = value.split("New England Journal of Medicine");
                if (parts.length !== 2) return value;
                return (
                  <>
                    {parts[0]}
                    <em>New England Journal of Medicine</em>
                    {parts[1]}
                  </>
                );
              }}
            </EditableText>
            <EditableText as="p" contentKey="about.hero.bio2" multiline defaultValue="Dr. Fitzer has performed more than 6,000 laparoscopic weight-loss operations and managed weight-loss medication treatment for more than 10,000 patients in the 21+ years that he has been helping people reach their weight-loss goals.">
              {(value) => {
                const opsPhrase = "more than 6,000 laparoscopic weight-loss operations";
                const medPhrase = "weight-loss medication treatment";
                const linkPhrase = (text: string, phrase: string, to: string, key: string): React.ReactNode => {
                  const parts = text.split(phrase);
                  if (parts.length !== 2) return text;
                  return (
                    <>
                      {parts[0]}
                      <Link key={key} to={to} className="text-brand-deep underline hover:no-underline">
                        {phrase}
                      </Link>
                      {parts[1]}
                    </>
                  );
                };
                // Link the operations phrase first, then the medication phrase inside
                // each resulting text part, so both links render.
                const opsParts = value.split(opsPhrase);
                if (opsParts.length !== 2) return value;
                return (
                  <>
                    {linkPhrase(opsParts[0], medPhrase, "/medical-weight-loss/medications", "med-0")}

                    <Link to="/services" className="text-brand-deep underline hover:no-underline">
                      {opsPhrase}
                    </Link>
                    {linkPhrase(opsParts[1], medPhrase, "/medical-weight-loss/medications", "med-1")}
                  </>
                );
              }}
            </EditableText>
          </div>
        </div>
      </PageSection>

      <PageSection fullWidth maxWidth="4xl" className="bg-brand-soft/30 pt-4 pb-4" innerClassName="space-y-6 text-foreground/80 leading-[1.6]">
        <EditableText as="h2" contentKey="about.training.title" defaultValue="Training & Background" className="text-2xl md:text-3xl font-serif text-foreground" />
        <EditableText as="p" contentKey="about.training.p1" multiline defaultValue="Dr. Fitzer grew up in Newport News, Virginia. He earned a B.S. in physics at Christopher Newport University and his M.D. at Eastern Virginia Medical School. He completed his general surgery residency at Memorial Health University Medical Center in Savannah, Georgia, where he became interested in weight loss surgery." />
        <EditableText as="p" contentKey="about.training.p2" multiline defaultValue="In 2005 he left Savannah for a year-long fellowship in minimally-invasive bariatric surgery at the University of Missouri, where he studied under nationally respected surgery masters including Steven Scott, Roger de la Torre, Bruce Ramshaw, and Steve Eubanks, a guru of laparoscopic hiatal hernia repair.">
          {(value) => {
            const phrase = "laparoscopic hiatal hernia repair";
            const parts = value.split(phrase);
            if (parts.length !== 2) return value;
            return (
              <>
                {parts[0]}
                <Link to="/services/hiatal-hernia-surgery" className="text-brand-deep underline hover:no-underline">
                  {phrase}
                </Link>
                {parts[1]}
              </>
            );
          }}
        </EditableText>
        <EditableText as="p" contentKey="about.training.p3" multiline defaultValue="In addition to his busy surgical practice, Dr. Fitzer has over twenty years of experience caring for patients who prefer a surgery-free weight-loss program. He has been prescribing injectable GLP-1 medications for weight loss since 2006. His surgical practice in Herndon, Virginia is built on the same principles he learned in fellowship: high-volume laparoscopic technique, conservative patient selection, and long-term follow-up." />
      </PageSection>

      <PageSection className="pt-4 pb-4 text-center">
        <EditableText as="h2" contentKey="about.cta.title" defaultValue="Ready to talk?" className="text-2xl md:text-3xl font-serif" />
        <EditableText as="p" contentKey="about.cta.body" defaultValue="It's a perfect day to get the ball rolling." className="mt-4 text-muted-foreground" />
        <div className="mt-8 flex flex-wrap justify-center gap-3">
          <Link to="/contact" className="rounded-full bg-primary px-7 py-3 text-sm font-medium text-primary-foreground hover:opacity-90">
            Contact Us
          </Link>
          <WeightLossOptionsButtons />
        </div>
      </PageSection>
    </>
  );
}
